import * as React from 'react';
import { isEmpty, isNil } from 'lodash';
import isEqual from 'lodash/isEqual';

import InlineEditingWrapper from 'components/inline-editing-wrapper/InlineEditingWrapper';
import Input from 'components/inputs/input/Input';
import { Table, TableCell, TableContainer, TableHead, TableRow } from 'components/table';

// $FlowIgnore
import InlineEditingFieldsContext from '../../../../../context/InlineEditingFieldsContext';

import { __t, applyTranslations } from 'utils/language';

import type { AdminAnalytic, DeliverableTikTokAnalytic } from 'types';

import './deliverables-metrics-tiktok-video-form.scss';

applyTranslations(require('./deliverable-metrics-tiktok-video-form.i18n.json'));

type Analytic = {
  reach: number | undefined | null,
  avgWatchTimeSec: number | undefined | null,
  // Because of difference Analytic (for TikTok) and AdminAnalytic
  impressions?: number | null,
  metricsType: string
};

type State = {
  metric: Analytic,
  inlineEditingFields: Array<any>
};

type Props = {
  deliverableId: number,
  campaignId: number,
  analytic: DeliverableTikTokAnalytic | undefined | null,
  onUpdate: (deliverableId: number, campaignId: number, analytic: Partial<AdminAnalytic>) => Promise<void>
};

class DeliverablesMetricsTikTokVideoForm extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      metric: this.getDefaultMetricState(this.props.analytic),
      inlineEditingFields: []
    };
  }

  componentDidUpdate(prevProps: Props) {
    if (!isEqual(this.props.analytic, prevProps?.analytic)) {
      this.setState({ // eslint-disable-line react/no-did-update-set-state
        metric: this.getDefaultMetricState(this.props.analytic)
      });
    }
  }

  getDefaultMetricState = (analytic?: DeliverableTikTokAnalytic | null) => ({
    reach: analytic?.reach || 0,
    avgWatchTimeSec: analytic?.avgWatchTimeSec || 0,
    metricsType: 'tiktok_video_metrics',
    // Because of difference Analytic (for TikTok) and AdminAnalytic
    impressions: undefined
  });

  handleInputChange = (name: string) => {
    const { deliverableId, campaignId, onUpdate } = this.props;

    return (value: any) => {
      this.setState(state => ({
        metric: {
          ...state.metric,
          [String(name)]: value && !isNaN(Number(value)) ? Number(value) : 0
        }
      }),
      () => onUpdate(deliverableId, campaignId, {
        ...this.state.metric,
        saves: undefined,
        profileVisits: undefined
      }));
    };
  };

  getInlineEditingFieldsContext() {
    return {
      registerInlineEditingField: this.registerInlineEditingField,
      unregisterInlineEditingField: this.unregisterInlineEditingField
    };
  }

  isInGlobalInlineEditMode() {
    const { inlineEditingFields } = this.state;

    return !isEmpty(inlineEditingFields);
  }

  registerInlineEditingField = (component: any): void => {
    if (component) {
      this.setState(state => ({
        inlineEditingFields: [...state.inlineEditingFields, component]
      }));
    }
  };

  unregisterInlineEditingField = (component: any): void => {
    if (!component) {
      return;
    }

    this.setState(state => ({
      inlineEditingFields: state.inlineEditingFields.filter(field => field !== component)
    }));
  };

  renderTextView = (value?: number | null) => () => (
    <span>
      {value || 0}
    </span>
  );

  renderTextEdit = (value?: string | null) => () => (
    <Input
      value={isNil(value) ? '' : value} // will be overwritten in InlineEditingWrapper
      width="short"
      onChange={() => {}}
    />
  );

  renderAnalyticInfo() {
    const { metric } = this.state;
    const inGlobalEditMode = this.isInGlobalInlineEditMode();

    return (
      <InlineEditingFieldsContext.Provider value={this.getInlineEditingFieldsContext()}>
        <TableRow>
          {
            ['reach', 'avgWatchTimeSec'].map(id => (
              <TableCell
                key={id}
                width="mini"
              >
                <InlineEditingWrapper
                  saveOnBlur
                  onSubmit={this.handleInputChange(id)}
                  renderViewMode={this.renderTextEdit(metric[id])}
                  renderEditMode={this.renderTextEdit(metric[id])}
                  initialValue={metric[id]}
                  inForceEditMode
                  inGlobalEditMode={inGlobalEditMode}
                />
              </TableCell>
            ))
          }
        </TableRow>
      </InlineEditingFieldsContext.Provider>
    );
  }

  render(): React.ReactNode {
    return (
      <section className="deliverables-metrics-tiktok-video-form">
        <TableContainer>
          <Table style="none">
            <TableRow>
              <TableHead width="mini">{__t('deliverable_metrics_tiktok_video_form.label_reach')}</TableHead>
              <TableHead width="mini">{__t('deliverable_metrics_tiktok_video_form.label_avg_watch_time')}</TableHead>
            </TableRow>
          </Table>
          {this.renderAnalyticInfo()}
        </TableContainer>
      </section>
    );
  }
}

export default DeliverablesMetricsTikTokVideoForm;
